import React from 'react';
import PropTypes from 'prop-types';
import { AddressValidationAlert } from '../components/address-validation/AddressValidationAlert';
import { AddressValidationOption } from '../components/address-validation/AddressValidationOption';
import { AddressValidationPrimaryButton } from '../components/address-validation/AddressValidationPrimaryButton';
import { ADDRESS_VALIDATION_MESSAGES } from '../constants/addressValidationMessages';
import { useAddressValidationData } from '../hooks/useAddressValidationData';
import { useAddressValidationFocus } from '../hooks/useAddressValidationFocus';
import { useAddressValidationHandlers } from '../hooks/useAddressValidationHandlers';
import { useTransactionPolling } from '../hooks/useTransactionPolling';
import { useUpdateProfileChoice } from '../hooks/useUpdateProfileChoice';
import { getValidationMessageKey } from '../util';

/**
 * Intentional deviations from the class are marked with "PORT NOTE" comments in
 * the hooks, helpers and components this composes. Three are worth naming here:
 *
 *   1. useTransactionPolling falls back to a 2000ms interval where the class
 *      used 1000ms. window.VetsGov.pollTimeout is set in practice, so the
 *      fallback is unreached.
 *   2. useTransactionPolling also dismisses the MHV email-confirmation cookie
 *      when an email transaction succeeds. Inert here — addressValidationType
 *      is always an address field, never FIELD_NAMES.EMAIL.
 *   3. suggestedAddresses and confirmedSuggestions are defaulted to [] below.
 *      getValidationMessageKey guards `suggestedAddresses?.length` but then
 *      calls `suggestedAddresses.filter(...)` unguarded, so an unpopulated store
 *      throws. The class leaned on a required propType to rule that out; this
 *      component renders against whatever the store holds.
 */
export const AddressValidationView2 = ({
  refreshTransaction,
  successCallback,
  transaction,
  transactionError,
}) => {
  const addressValidation = useAddressValidationData({ transaction });

  const {
    addressFromUser,
    addressValidationError,
    addressValidationErrorCode,
    addressValidationType,
    confirmedSuggestions = [],
    isLoading,
    isNoValidationKeyAlertEnabled,
    overrideValidationKey,
    selectedAddressId,
    suggestedAddresses = [],
  } = addressValidation;

  const updateProfileChoice = useUpdateProfileChoice({
    vapServiceFormFields: addressValidation.vapServiceFormFields,
  });

  const { onChangeSelectedAddress, onEditClick, onSubmit } =
    useAddressValidationHandlers({
      ...addressValidation,
      successCallback,
      updateProfileChoice,
    });

  useTransactionPolling({
    transaction,
    transactionRequest: undefined,
    fieldName: addressValidationType,
    onRefresh: refreshTransaction,
  });

  useAddressValidationFocus(addressValidationType);

  const validationMessageKey = getValidationMessageKey({
    suggestedAddresses,
    addressValidationError,
    confirmedSuggestions,
    overrideValidationKey,
    addressValidationErrorCode,
    isNoValidationKeyAlertEnabled, // remove when profileShowNoValidationKeyAddressAlert flag is retired
  });

  const shouldShowSuggestions = confirmedSuggestions.length > 0;

  return (
    <>
      {/*
      Show the address validation alert when there is no service transaction error.
      A transaction error alert will appear in the element position right before this component and
      under the field component header.
      */}
      {!transactionError && (
        <AddressValidationAlert
          addressValidationError={addressValidationError}
          message={ADDRESS_VALIDATION_MESSAGES[validationMessageKey]}
          onEditClick={onEditClick}
        />
      )}
      <form onSubmit={onSubmit}>
        <AddressValidationOption
          address={addressFromUser}
          confirmedSuggestions={confirmedSuggestions}
          onChangeSelectedAddress={onChangeSelectedAddress}
          overrideValidationKey={overrideValidationKey}
          selectedAddressId={selectedAddressId}
        />
        {shouldShowSuggestions && (
          <AddressValidationOption
            address={{}}
            confirmedSuggestions={confirmedSuggestions}
            id="suggested"
            onChangeSelectedAddress={onChangeSelectedAddress}
            overrideValidationKey={overrideValidationKey}
            selectedAddressId={selectedAddressId}
          />
        )}

        <div className="vads-u-display--flex mobile-lg:vads-u-display--block vads-u-flex-direction--column">
          <AddressValidationPrimaryButton
            addressValidationError={addressValidationError}
            confirmedSuggestions={confirmedSuggestions}
            isLoading={isLoading}
            onEditClick={onEditClick}
            overrideValidationKey={overrideValidationKey}
            selectedAddressId={selectedAddressId}
          />
          {!addressValidationError && !isLoading && (
            <va-button
              data-testid="edit-address-button"
              onClick={onEditClick}
              label="Edit address"
              text="Edit"
              class="vads-u-margin-top--1 vads-u-width--full mobile-lg:vads-u-width--auto"
              secondary
            />
          )}
        </div>
      </form>
    </>
  );
};

AddressValidationView2.propTypes = {
  refreshTransaction: PropTypes.func,
  successCallback: PropTypes.func,
  transaction: PropTypes.object,
  transactionError: PropTypes.shape({
    errors: PropTypes.arrayOf(
      PropTypes.shape({
        code: PropTypes.string,
        message: PropTypes.string,
      }),
    ),
  }),
};

export default AddressValidationView2;
